import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { PanelSubscription } from '@storeo/api-client';
import { api } from '../lib/api';
import shellStyles from '../components/shell/shell.module.css';
import ui from '../styles/ui.module.css';

const STATUS_LABEL: Record<string, string> = {
  active: 'Ativa',
  past_due: 'Pagamento atrasado',
  expired: 'Expirada',
  canceled: 'Cancelada',
};

const STATUS_PILL: Record<string, string> = {
  active: 'pillPos',
  past_due: 'pillWarn',
  expired: 'pillMuted',
  canceled: 'pillMuted',
};

function money(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function when(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR');
}

/** P13/P14 · Assinantes — lista + cancelar (camada L5). */
export function Assinantes() {
  const qc = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);
  const subs = useQuery({ queryKey: ['subscriptions'], queryFn: () => api.subscriptions() });

  const cancel = useMutation({
    mutationFn: (s: PanelSubscription) => api.cancelSubscription(s.id, 'cancelado pelo parceiro'),
    onMutate: (s) => setBusyId(s.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['subscriptions'] }),
    onSettled: () => setBusyId(null),
  });

  return (
    <>
      <div className={shellStyles.head}>
        <h1>Assinantes</h1>
        <p>Quem paga toda mês pelas suas assinaturas.</p>
      </div>

      <div className={ui.card}>
        {!subs.data?.length ? (
          <p className={ui.empty}>Nenhum assinante ainda.</p>
        ) : (
          <div className={ui.tblScroll}>
            <table className={ui.tbl}>
              <thead>
                <tr>
                  <th>Cliente</th>
                  <th>Produto</th>
                  <th className="r">Valor</th>
                  <th>Meio</th>
                  <th>Status</th>
                  <th>Próxima cobrança</th>
                  <th className="r">Ações</th>
                </tr>
              </thead>
              <tbody>
                {subs.data.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <b>{s.customerName || s.customerEmail}</b>
                      <div className={ui.faint}>{s.customerEmail}</div>
                    </td>
                    <td className={ui.muted}>{s.productName}</td>
                    <td className="r">
                      <span className={ui.money}>{money(s.amount)}/mês</span>
                    </td>
                    <td className={ui.mono}>{s.paymentMethod === 'card' ? 'CARTÃO' : 'PIX'}</td>
                    <td>
                      <span className={`${ui.pill} ${ui[STATUS_PILL[s.status] ?? 'pillMuted']}`}>
                        {STATUS_LABEL[s.status] ?? s.status}
                      </span>
                    </td>
                    <td className={ui.faint}>{s.status === 'active' ? when(s.nextChargeAt) : '—'}</td>
                    <td className="r">
                      {s.status === 'active' ? (
                        <button
                          className={`${ui.btn} ${ui.btnGhost} ${ui.btnSmall}`}
                          disabled={busyId === s.id}
                          onClick={() => cancel.mutate(s)}
                        >
                          Cancelar
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
